/* eslint-disable max-statements */
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ConfigurationsService } from '@icc/configurator/shared';
import { DoorActiveConfiguration, ComponentsServiceBase, ComponentsState, ComponentAvailability } from '@icc/window-door/types';
import { PriceService } from '@icc/price/b2b';
import { StateService, EventBusService, TranslateService, BaseIssuesService } from '@icc/shared';
import { DrawService } from '@icc/configurator/shared';
import { IssueLevel, core } from '@icc/shared/helpers';
import { PriceAccessoryService } from '@icc/construction-elements/configurator';
import { CustomPricesService } from '@icc/shared/custom-price/custom-prices.service';

@Injectable({
    providedIn: 'root'
})
export class ComponentsService extends ComponentsServiceBase {
    private componentsStateSubject = new BehaviorSubject<ComponentsState>(this.getInitialState());
    public componentsState$ = this.componentsStateSubject.asObservable();

    constructor(
        private configurationsService: ConfigurationsService<'door'>,
        private priceService: PriceService,
        private eventBusService: EventBusService,
        public drawService: DrawService,
        private issuesService: BaseIssuesService,
        private translateService: TranslateService,
        private priceAccessoryService: PriceAccessoryService,
        private stateService: StateService,
        private customPricesService: CustomPricesService,
    ) {
         super();
         this.eventBusService.subscribe(
            [
                'changedSashes',
                'changedFrames',
                'changedFillings',
                'setExtensionProfile',
                'setFrameProfile',
                'setSashProfile',
                'setMullionProfile',
                'putAlignmentInField',
                'removedAlignmentInField',
                'removedAlignment',
                'putExtensionOnSide',
                'removedExtensionFromSide',
                'setLowThreshold',
                'unsetLowThreshold',
                'insertMuntins',
                'removeMuntins',
                'updateMuntins',
                'setMuntinColor',
                'setConstructionColor',
                'changedFinWidths',
                'setGlazingInSash',
                'setFillingColor',
                'changedDoorHardware',
                'setThresholdColor',
                'setLippingColor',
                'configurationChanged',
                'changedConfigurator',
                'setDoorPortalColor',
                'rollerBoxHeightChanged',
                'changedShutter',
                'setPanelTrim',
            ],
            data => {
                try{
                    this.validateState();
                } catch (err){
                    console.error(err);
                }
            }
        );

        this.eventBusService.subscribe('updatedPrice', data => {
            try{
                if (DoorActiveConfiguration.is(this.configurationsService.conf.Current)) {
                    this.checkIssuesOfComponents();
                    this.areAllAvailableComponentsUnchecked();
                }
            } catch (err){
                console.error(err);
            }
        });

        this.eventBusService.subscribe<{ systemId: number; newSystem: boolean; defaultSystem: boolean; oldSystemId?: number }>('setSystem', data => {
            try{
                if(data?.value && data.value.oldSystemId !== data.value.systemId) {
                    const currentState = { ...this.getCurrentState() };
                    this.updateAvailability(currentState);
                    this.checkAllAvailableComponents(currentState);
                    this.componentsStateSubject.next(currentState);
                    this.updateConfigurationComponents(currentState);
                }
            } catch (err){
                console.error(err);
            }
        });

    }

    private getInitialState(): ComponentsState {
        const conf = this.configurationsService?.conf?.Current;

        if (DoorActiveConfiguration.is(conf) && conf?.Components) {
            const components = conf.Components;
            return {
                fullDoor: { checked: components.fullDoor ?? true, available: this.isFullDoorAvailable(conf) },
                activeSash: { checked: components.activeSash ?? true, available: this.isActiveSashAvailable(conf) },
                passiveSash: { checked: components.passiveSash ?? true, available: this.isPassiveSashAvailable(conf) },
                frame: { checked: components.frame ?? true, available: this.isFrameAvailable(conf) },
                threshold: { checked: components.threshold ?? true, available: this.isThresholdAvailable(conf) },
                hardware: { checked: components.hardware ?? true, available: this.isHardwareAvailable(conf) },
                sideLight: {
                    left: { checked: components.sideLight?.left ?? true, available: this.isSideLightLeftAvailable(conf) },
                    right: { checked: components.sideLight?.right ?? true, available: this.isSideLightRightAvailable(conf) },
                    top: { checked: components.sideLight?.top ?? true, available: this.isSideLightTopAvailable(conf) }
                },
                extension: {
                    left: { checked: components.extension?.left ?? true, available: this.isExtensionLeftAvailable(conf) },
                    right: { checked: components.extension?.right ?? true, available: this.isExtensionRightAvailable(conf) },
                    top: { checked: components.extension?.top ?? true, available: this.isExtensionTopAvailable(conf) }
                },
                bottomExtension: { checked: components.bottomExtension ?? true, available: this.isBottomExtensionAvailable(conf) },
                portal: { checked: components.portal ?? true, available: this.isPortalAvailable(conf) },
                casing: { checked: components.casing ?? true, available: this.isCasingAvailable(conf) },
                accessories: { checked: components.accessories ?? true, available: this.isAccessoriesAvailable(conf) },
                pull: { checked: components.pull ?? true, available: this.isPullAvailable(conf) },
                recessedPull: { checked: components.recessedPull ?? true, available: this.isRecessedAvailable(conf) }
            };
        }

        return {
            fullDoor: { checked: true, available: true },
            activeSash: { checked: true, available: true },
            passiveSash: { checked: true, available: true },
            frame: { checked: true, available: true },
            threshold: { checked: true, available: true },
            hardware: { checked: true, available: true },
            sideLight: {
                left: { checked: true, available: true },
                right: { checked: true, available: true },
                top: { checked: true, available: true }
            },
            extension: {
                left: { checked: true, available: true },
                right: { checked: true, available: true },
                top: { checked: true, available: true }
            },
            bottomExtension: { checked: true, available: true },
            portal: { checked: true, available: true },
            casing: { checked: true, available: true },
            accessories: { checked: true, available: true },
            pull: { checked: true, available: true },
            recessedPull: { checked: true, available: true }
        };
    }

    getCurrentState(): ComponentsState {
        return this.componentsStateSubject.value;
    }

    updateComponentState(path: string, checked: boolean): void {
        const currentState = { ...this.getCurrentState() };
        this.setNestedProperty(currentState, path + '.checked', checked);
        this.componentsStateSubject.next(currentState);

        this.updateConfigurationComponents(currentState);
    }

    private updateConfigurationComponents(state: ComponentsState): void {
        if (this.configurationsService?.conf?.Current && DoorActiveConfiguration.is(this.configurationsService.conf.Current)) {
            const components = this.stateToComponents(state);
            this.configurationsService.conf.Current.Components = components;
            this.drawService.init();
            this.priceService.count();
            this.eventBusService.post({ key: 'processDependencies', value: null });
        }
    }

    private checkIssuesOfComponents() {
         if(this.configurationsService.conf.Current.Price !== 0 && this.configurationsService.conf.Current.Issues.find(issue => issue.key === 'incorrect-components')) {
            this.issuesService.unregister('incorrect-components', this.configurationsService.conf.Current);
            this.priceService.count();
        } else if(this.configurationsService.conf.Current.Price === 0){
            this.issuesService.simpleRegister(
                'incorrect-components',
                'Wybranych komponentów nie można zamówić, nie posiadają ceny',
                this.translateService.instant(
                    'DOOR|Wybranych komponentów nie można zamówić, nie posiadają ceny.'
                ),
                this.configurationsService.conf.Current,
                {
                    blockAddToOffer: true,
                    showMessage: false,
                    logLevel: IssueLevel.NONE,
                }
            );
        }

    }

    private stateToComponents(state: ComponentsState): any {
        return {
            fullDoor: state.fullDoor.checked,
            activeSash: state.activeSash.checked,
            passiveSash: state.passiveSash.checked,
            frame: state.frame.checked,
            threshold: state.threshold.checked,
            hardware: state.hardware.checked,
            sideLight: {
                left: state.sideLight.left.checked,
                right: state.sideLight.right.checked,
                top: state.sideLight.top.checked
            },
            extension: {
                left: state.extension.left.checked,
                right: state.extension.right.checked,
                top: state.extension.top.checked
            },
            bottomExtension: state.bottomExtension.checked,
            portal: state.portal.checked,
            casing: state.casing.checked,
            accessories: state.accessories.checked,
            pull: state.pull.checked,
            recessedPull: state.recessedPull.checked
        };
    }

    validateState() {
        const currentState = { ...this.getCurrentState() };
        let stateChanged = false;
        const stateBeforeUpdate = core.copy(currentState);
        this.updateAvailability(currentState);

        const sideLightComponents = ['sideLight.left', 'sideLight.right', 'sideLight.top'];
        const sideLightChanges = this.hasMultipleComponentsAvailabilityChanged(
            sideLightComponents,
            stateBeforeUpdate,
            currentState
        );
        if(sideLightChanges['sideLight.left'] || sideLightChanges['sideLight.right'] || sideLightChanges['sideLight.top']) {
            this.checkAllAvailableComponents(currentState);
            stateChanged = true;
        }

        if (!currentState.fullDoor.checked && !currentState.fullDoor.available) {
            currentState.fullDoor.checked = true;
            stateChanged = true;
        }

        if (!currentState.activeSash.checked && !currentState.activeSash.available) {
            currentState.activeSash.checked = true;
            stateChanged = true;
        }

        if (!currentState.passiveSash.checked && !currentState.passiveSash.available) {
            currentState.passiveSash.checked = true;
            stateChanged = true;
        }

        if (!currentState.frame.checked && !currentState.frame.available) {
            currentState.frame.checked = true;
            stateChanged = true;
        }

        if (!currentState.threshold.checked && !currentState.threshold.available) {
            currentState.threshold.checked = true;
            stateChanged = true;
        }

        if (!currentState.hardware.checked && !currentState.hardware.available) {
            currentState.hardware.checked = true;
            stateChanged = true;
        }

        if (!currentState.sideLight.left.checked && !currentState.sideLight.left.available) {
            currentState.sideLight.left.checked = true;
            stateChanged = true;
        }

        if (!currentState.sideLight.right.checked && !currentState.sideLight.right.available) {
            currentState.sideLight.right.checked = true;
            stateChanged = true;
        }

        if (!currentState.sideLight.top.checked && !currentState.sideLight.top.available) {
            currentState.sideLight.top.checked = true;
            stateChanged = true;
        }

        if (!currentState.extension.left.checked && !currentState.extension.left.available) {
            currentState.extension.left.checked = true;
            stateChanged = true;
        }

        if (!currentState.extension.right.checked && !currentState.extension.right.available) {
            currentState.extension.right.checked = true;
            stateChanged = true;
        }

        if (!currentState.extension.top.checked && !currentState.extension.top.available) {
            currentState.extension.top.checked = true;
            stateChanged = true;
        }

        if (!currentState.bottomExtension.checked && !currentState.bottomExtension.available) {
            currentState.bottomExtension.checked = true;
            stateChanged = true;
        }

        if (!currentState.portal.checked && !currentState.portal.available) {
            currentState.portal.checked = true;
            stateChanged = true;
        }

        if (!currentState.casing.checked && !currentState.casing.available) {
            currentState.casing.checked = true;
            stateChanged = true;
        }

        if (!currentState.accessories.checked && !currentState.accessories.available) {
            currentState.accessories.checked = true;
            stateChanged = true;
        }

        if (!currentState.pull.checked && !currentState.pull.available) {
            currentState.pull.checked = true;
            stateChanged = true;
        }

        if (!currentState.recessedPull.checked && !currentState.recessedPull.available) {
            currentState.recessedPull.checked = true;
            stateChanged = true;
        }

        if (stateChanged) {
            this.componentsStateSubject.next(currentState);
            this.updateConfigurationComponents(currentState);
        }
    }

    updateComponentsFromConfiguration(components: any): void {
        const currentState = { ...this.getCurrentState() };

        currentState.fullDoor.checked = components.fullDoor;
        currentState.activeSash.checked = components.activeSash;
        currentState.passiveSash.checked = components.passiveSash;
        currentState.frame.checked = components.frame;
        currentState.threshold.checked = components.threshold;
        currentState.hardware.checked = components.hardware;
        currentState.sideLight.left.checked = components.sideLight?.left || false;
        currentState.sideLight.right.checked = components.sideLight?.right || false;
        currentState.sideLight.top.checked = components.sideLight?.top || false;
        currentState.extension.left.checked = components.extension?.left || false;
        currentState.extension.right.checked = components.extension?.right || false;
        currentState.extension.top.checked = components.extension?.top || false;
        currentState.bottomExtension.checked = components.bottomExtension;
        currentState.portal.checked = components.portal;
        currentState.casing.checked = components.casing;
        currentState.accessories.checked = components.accessories;
        currentState.pull.checked = components.pull;
        currentState.recessedPull.checked = components.recessedPull;

        this.updateAvailability(currentState);

        this.componentsStateSubject.next(currentState);

        this.updateConfigurationComponents(currentState);
    }

    private updateAvailability(state: ComponentsState): void {
        if (!this.configurationsService?.conf?.Current || !DoorActiveConfiguration.is(this.configurationsService.conf.Current)) {
            return;
        }

        state.fullDoor.available = this.isFullDoorAvailable();
        state.activeSash.available = this.isActiveSashAvailable();
        state.passiveSash.available = this.isPassiveSashAvailable();
        state.frame.available = this.isFrameAvailable();
        state.threshold.available = this.isThresholdAvailable();
        state.hardware.available = this.isHardwareAvailable();
        state.sideLight.left.available = this.isSideLightLeftAvailable();
        state.sideLight.right.available = this.isSideLightRightAvailable();
        state.sideLight.top.available = this.isSideLightTopAvailable();
        state.extension.left.available = this.isExtensionLeftAvailable();
        state.extension.right.available = this.isExtensionRightAvailable();
        state.extension.top.available = this.isExtensionTopAvailable();
        state.bottomExtension.available = this.isBottomExtensionAvailable();
        state.portal.available = this.isPortalAvailable();
        state.casing.available = this.isCasingAvailable();
        state.accessories.available = this.isAccessoriesAvailable();
        state.pull.available = this.isPullAvailable();
        state.recessedPull.available = this.isRecessedAvailable();
    }

    private hasComponentAvailabilityChanged(
        componentPath: string,
        stateBefore: ComponentsState,
        stateAfter: ComponentsState
    ): boolean {
        const availabilityBefore = this.getNestedAvailability(stateBefore, componentPath);
        const availabilityAfter = this.getNestedAvailability(stateAfter, componentPath);
        return availabilityBefore !== availabilityAfter;
    }

    private hasMultipleComponentsAvailabilityChanged(
        componentPaths: string[],
        stateBefore: ComponentsState,
        stateAfter: ComponentsState
    ): { [componentPath: string]: boolean } {
        const changes: { [componentPath: string]: boolean } = {};

        componentPaths.forEach(path => {
            changes[path] = this.hasComponentAvailabilityChanged(path, stateBefore, stateAfter);
        });

        return changes;
    }

    private getNestedAvailability(state: ComponentsState, path: string): boolean {
        const keys = path.split('.');
        let current: ComponentsState | ComponentAvailability | { left: ComponentAvailability; right: ComponentAvailability; top: ComponentAvailability } = state;

        for (const key of keys) {
            current = (current as any)[key];
        }

        return (current as ComponentAvailability).available;
    }

    private setNestedProperty(obj: any, path: string, value: any): void {
        const keys = path.split('.');
        let current = obj;
        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
    }

    isFullDoorAvailable(conf = this.configurationsService.conf.Current): boolean {
        return true;
    }

    isActiveSashAvailable(conf = this.configurationsService.conf.Current): boolean {
        return true;
    }

    isPassiveSashAvailable(conf = this.configurationsService.conf.Current): boolean {
        return conf.Sashes.some(sash => ['DRP', 'DOP'].indexOf(sash.type.type) > -1);
    }

    isFrameAvailable(conf = this.configurationsService.conf.Current): boolean {
        return true;
    }

    isThresholdAvailable(conf = this.configurationsService.conf.Current): boolean {
        return true;
    }

    isHardwareAvailable(conf = this.configurationsService.conf.Current): boolean {
        return true;
    }

    isSideLightLeftAvailable(conf = this.configurationsService.conf.Current): boolean {
        return conf.OwnedSashesTypes.doorLeftLight;

    }

    isSideLightRightAvailable(conf = this.configurationsService.conf.Current): boolean {
        return conf.OwnedSashesTypes.doorRightLight;
    }

    isSideLightTopAvailable(conf = this.configurationsService.conf.Current): boolean {
        return conf.OwnedSashesTypes.doorTopLight;

    }

    isExtensionLeftAvailable(conf = this.configurationsService.conf.Current): boolean {
        return conf.SideProfiles.some(profile => profile.side === 'left');
    }

    isExtensionRightAvailable(conf = this.configurationsService.conf.Current): boolean {
        return conf.SideProfiles.some(profile => profile.side === 'right');

    }

    isExtensionTopAvailable(conf = this.configurationsService.conf.Current): boolean {
        return conf.SideProfiles.some(profile => profile.side === 'top');

    }

    isBottomExtensionAvailable(conf = this.configurationsService.conf.Current): boolean {
        return conf.SideProfiles.some(profile => profile.side === 'bottom');

    }

    isPortalAvailable(conf = this.configurationsService.conf.Current): boolean {
        return Boolean(conf.doorPortal);
    }

    isCasingAvailable(conf = this.configurationsService.conf.Current): boolean {
        return Boolean(conf.Casing.id);
    }

    isAccessoriesAvailable(conf = this.configurationsService.conf.Current): boolean {
        const offer = this.stateService.getCurrentOffer();
        const dealerId = offer ? offer.dealer_id : null;
        const customPrice = this.customPricesService.get(dealerId);
        return conf.Accessories.some(accessory => this.priceAccessoryService.priceAccessory(
                    accessory,
                    conf.Colors || null,
                    (customPrice || {}).WindowAccessory,
                    conf.SideAccessories ? conf.SideAccessories.sizes : undefined,
                    conf.Width,
                    conf.Height,
                    conf.type
                ) > 0
            );

    }

    isPullAvailable(conf = this.configurationsService.conf.Current): boolean {
        return Boolean(conf.doorHardware.pull);
    }

    isRecessedAvailable(conf = this.configurationsService.conf.Current): boolean {
        return Boolean(conf.doorHardware.recessedPull);
    }

    private areAllAvailableComponentsUnchecked(): void {
        const currentState = { ...this.getCurrentState() };
        this.updateAvailability(currentState);
        const availableComponents = [
            currentState.fullDoor,
            currentState.activeSash,
            currentState.passiveSash,
            currentState.frame,
            currentState.threshold,
            currentState.hardware,
            currentState.sideLight.left,
            currentState.sideLight.right,
            currentState.sideLight.top,
            currentState.extension.left,
            currentState.extension.right,
            currentState.extension.top,
            currentState.bottomExtension,
            currentState.portal,
            currentState.casing,
            currentState.accessories,
            currentState.pull,
            currentState.recessedPull
        ];

        const anyComponentsChecked = availableComponents.some(component =>
            component.available && component.checked
        );
        if(!anyComponentsChecked) {
            this.checkAllAvailableComponents(currentState);
            this.componentsStateSubject.next(currentState);
            this.updateConfigurationComponents(currentState);
        }
    }

    private checkAllAvailableComponents(state: ComponentsState): void {
        if (state.fullDoor.available) state.fullDoor.checked = true;
        if (state.activeSash.available) state.activeSash.checked = true;
        if (state.passiveSash.available) state.passiveSash.checked = true;
        if (state.frame.available) state.frame.checked = true;
        if (state.threshold.available) state.threshold.checked = true;
        if (state.hardware.available) state.hardware.checked = true;
        if (state.sideLight.left.available) state.sideLight.left.checked = true;
        if (state.sideLight.right.available) state.sideLight.right.checked = true;
        if (state.sideLight.top.available) state.sideLight.top.checked = true;
        if (state.extension.left.available) state.extension.left.checked = true;
        if (state.extension.right.available) state.extension.right.checked = true;
        if (state.extension.top.available) state.extension.top.checked = true;
        if (state.bottomExtension.available) state.bottomExtension.checked = true;
        if (state.portal.available) state.portal.checked = true;
        if (state.casing.available) state.casing.checked = true;
        if (state.accessories.available) state.accessories.checked = true;
        if (state.pull.available) state.pull.checked = true;
        if (state.recessedPull.available) state.recessedPull.checked = true;
    }
}
